<template>
  <gs-card class="gs-traffic">
    <div class="gs-traffic__head">
      <div>
        <h2 class="gs-traffic__title">Traffic over time</h2>
        <p class="gs-traffic__subtitle">{{ activeMetric.note }}</p>
      </div>

      <div class="gs-traffic__switch" role="group" aria-label="Chart metric">
        <button
          v-for="option in metrics"
          :key="option.value"
          type="button"
          :data-metric="option.value"
          :class="['gs-traffic__switch-item', { 'is-active': option.value === metric }]"
          :aria-pressed="option.value === metric ? 'true' : 'false'"
          @click="$emit('update:metric', option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div class="gs-traffic__plot">
      <p v-if="loading" class="gs-traffic__state">Loading traffic…</p>
      <p v-else-if="!hasData" class="gs-traffic__state">No traffic in this range</p>
      <vue-apex-charts
        v-else
        type="area"
        height="310"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </gs-card>
</template>

<script>
export const CHART_METRICS = [
  { value: 'plays', label: 'Plays', note: 'Playback starts per reporting period.' },
  { value: 'visitors', label: 'Visitors', note: 'Summed viewer counts per reporting period.' },
  { value: 'finishes', label: 'Finishes', note: 'Playback sessions reaching the end per reporting period.' },
  { value: 'watches', label: 'Watch time', note: 'Time watched per reporting period.' },
];

export default {
  name: 'TrafficChart',
  props: {
    series: { type: Array, default: () => [] },
    metric: { type: String, default: 'plays' },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      metrics: CHART_METRICS,
      // Filled at mount from the kit's :root tokens — there are no chart
      // colour tokens, so the series is the brand pink and the chrome is the
      // two neutral tones the rest of the page already uses.
      accent: 'currentColor',
      axis: 'currentColor',
      grid: 'currentColor',
    };
  },
  computed: {
    activeMetric() {
      return CHART_METRICS.find(option => option.value === this.metric) || CHART_METRICS[0];
    },
    points() {
      return (this.series || []).filter(point => point && point.x !== undefined);
    },
    hasData() {
      return this.points.some(point => Number(point.y) > 0);
    },
    chartSeries() {
      return [{ name: this.activeMetric.label, data: this.points.map(point => ({ x: point.x, y: Number(point.y) || 0 })) }];
    },
    chartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: [this.accent],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 0, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 100] },
        },
        grid: { borderColor: this.grid, strokeDashArray: 4, padding: { left: 4, right: 4 } },
        xaxis: {
          type: 'category',
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: this.axis, fontSize: '12px' }, rotate: 0, hideOverlappingLabels: true },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: { style: { colors: this.axis, fontSize: '12px' } },
        },
        tooltip: { theme: 'light' },
        legend: { show: false },
      };
    },
  },
  mounted() {
    this.readTokens();
  },
  methods: {
    readTokens() {
      if (typeof window === 'undefined' || !window.getComputedStyle) return;
      const styles = window.getComputedStyle(this.$el.closest('.gs-app') || this.$el);
      const read = (name, fallback) => (styles.getPropertyValue(name) || '').trim() || fallback;
      this.accent = read('--gs-primary', this.accent);
      this.axis = read('--gs-muted-foreground', this.axis);
      this.grid = read('--gs-border', this.grid);
    },
  },
};
</script>

<style scoped>
.gs-traffic {
  padding: 20px 24px 16px;
}

.gs-traffic__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.gs-traffic__title {
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
}

.gs-traffic__subtitle {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-traffic__switch {
  display: inline-flex;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gs-traffic__switch-item {
  padding: 6px 12px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gs-traffic__switch-item:hover {
  color: var(--gs-foreground);
}

.gs-traffic__switch-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-traffic__switch-item.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gs-traffic__plot {
  min-height: 310px;
}

.gs-traffic__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 310px;
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}
</style>
